/**
 * MemoryConfidenceBar.jsx
 * BerBul AI Trading — Shared Intelligence Component
 *
 * Shows the memory system's prediction confidence for the most recent PROCEED
 * decision: historical target-hit rate + pattern similarity score + case count.
 *
 * Works in both contexts:
 *   TradingCommand  → pass liveBars (rolling 60-bar window); shows live last PROCEED
 *   DailyDetailDashboard → pass liveBars (full session); shows session aggregate
 *
 * Props:
 *   liveBars  {Array}   — bar array with eval_agent_story fields
 *   mode      {string}  — "live" (default) | "session"
 *                          live    = shows last PROCEED bar only
 *                          session = shows aggregate across all PROCEED bars
 *   compact   {boolean} — compact single-row layout (default false)
 *
 * No external dependencies. Pure BRD design system.
 */

import { useMemo } from "react";

// ── Design tokens (matches BRD / BotIntelligencePanel) ──────────────────────
const C = {
  bg:      "#08101E",
  surface: "#0C1525",
  s2:      "#101B2E",
  s3:      "#0e1828",
  border:  "#1E2D42",
  b2:      "#2a3d56",
  text:    "#E8EDF5",
  muted:   "#6A7D95",
  body:    "#B8C8DC",
  green:   "#00E896",
  cyan:    "#00C8FF",
  amber:   "#FFB340",
  red:     "#FF4466",
  blue:    "#4488FF",
  purple:  "#9966FF",
};

const MONO = "'DM Mono', 'Courier New', monospace";

// ── Helpers ──────────────────────────────────────────────────────────────────
function pct(v, decimals = 0) {
  if (v == null || isNaN(v)) return "—";
  return `${(v * 100).toFixed(decimals)}%`;
}

function hitRateColor(rate) {
  if (rate == null) return C.muted;
  if (rate >= 0.70) return C.green;
  if (rate >= 0.55) return C.cyan;
  if (rate >= 0.40) return C.amber;
  return C.red;
}

function simColor(sim) {
  if (sim == null) return C.muted;
  if (sim >= 0.80) return C.green;
  if (sim >= 0.65) return C.cyan;
  if (sim >= 0.50) return C.amber;
  return C.red;
}

// ── Memory extractor (matches DailyDetailDashboard extractMemory) ─────────────
function extractMemory(bar) {
  const story = bar?.eval_agent_story;
  if (!story) return null;
  const perf = story.performance || {};

  if (story.memory_summary) {
    const ms = story.memory_summary;
    return {
      targetHitRate: perf.target_hit_rate ?? ms.target_hit_rate ?? null,
      similarity:    perf.similarity      ?? ms.top_similarity  ?? ms.similarity_score ?? null,
      cases:         ms.found_cases       ?? 0,
      recent:        ms.recent_cases      ?? 0,
      historical:    ms.historical_cases  ?? 0,
      ev:            perf.expected_value  ?? null,
      supports:      perf.supports_decision ?? null,
      conflict:      false,
      note:          ms.note             ?? null,
      confLevel:     perf.confidence_level ?? null,
    };
  }
  if (story.memory) {
    const m = story.memory;
    return {
      targetHitRate: perf.target_hit_rate ?? m.target_hit_rate ?? null,
      similarity:    perf.similarity      ?? m.best_match       ?? null,
      cases:         m.found_cases        ?? 0,
      recent:        m.recent_cases       ?? 0,
      historical:    m.historical_cases   ?? 0,
      ev:            perf.expected_value  ?? null,
      supports:      perf.supports_decision ?? m.supports_decision ?? null,
      conflict:      m.memory_conflict    ?? false,
      note:          m.summary            ?? null,
      confLevel:     perf.confidence_level ?? m.confidence_level ?? null,
    };
  }
  return null;
}

// ── Bar component ─────────────────────────────────────────────────────────────
function MiniBar({ value, color, label, width = 120 }) {
  const pctVal = value != null ? Math.min(1, Math.max(0, value)) * 100 : 0;
  const hasData = value != null;
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 4, flex: 1 }}>
      <div style={{ display: "flex", justifyContent: "space-between" }}>
        <span style={{ fontFamily: MONO, fontSize: 8, color: C.muted, letterSpacing: "1px" }}>
          {label}
        </span>
        <span style={{ fontFamily: MONO, fontSize: 11, color: hasData ? color : C.muted, fontWeight: 500 }}>
          {hasData ? pct(value) : "—"}
        </span>
      </div>
      <div style={{ height: 4, background: C.s3, borderRadius: 2, overflow: "hidden" }}>
        {hasData && (
          <div style={{
            width: `${pctVal}%`, height: "100%",
            background: color, borderRadius: 2,
            transition: "width 0.5s ease",
          }} />
        )}
      </div>
    </div>
  );
}

// ── Stat pill ─────────────────────────────────────────────────────────────────
function StatPill({ label, value, color }) {
  return (
    <div style={{
      display: "flex", flexDirection: "column", alignItems: "center",
      background: C.s3, border: `1px solid ${C.border}`,
      borderRadius: 6, padding: "6px 12px", minWidth: 52,
    }}>
      <span style={{ fontFamily: MONO, fontSize: 14, color: color || C.body, fontWeight: 500, lineHeight: 1 }}>
        {value}
      </span>
      <span style={{ fontFamily: MONO, fontSize: 7, color: C.muted, letterSpacing: "0.8px", marginTop: 3 }}>
        {label}
      </span>
    </div>
  );
}

// ── Main component ─────────────────────────────────────────────────────────────
export default function MemoryConfidenceBar({ liveBars = [], mode = "live", compact = false }) {

  const data = useMemo(() => {
    const proceedBars = (liveBars || []).filter(b => {
      const dec = b.eval_decision || b.decision;
      return dec === "PROCEED" || dec === "BUY";
    });

    if (!proceedBars.length) return null;

    if (mode === "live") {
      // Last PROCEED bar only — what the bot just decided on
      const last = proceedBars[proceedBars.length - 1];
      const mem  = extractMemory(last);
      if (!mem) return null;
      return {
        mode: "live",
        targetHitRate: mem.targetHitRate,
        similarity:    mem.similarity,
        cases:         mem.cases,
        recent:        mem.recent,
        historical:    mem.historical,
        ev:            mem.ev,
        supports:      mem.supports,
        conflict:      mem.conflict,
        note:          mem.note,
        confLevel:     mem.confLevel,
        time: last.t
          ? new Date(last.t).toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit", hour12: false })
          : null,
      };
    }

    // session — aggregate across all PROCEED bars that have memory data
    const withMem = proceedBars.map(b => extractMemory(b)).filter(Boolean).filter(m => {
      const sim = parseFloat(m.similarity);
      return m.cases > 0 || (m.similarity != null && sim > 0);
    });

    if (!withMem.length) return null;

    const hitRates  = withMem.map(m => m.targetHitRate).filter(v => v != null && v > 0);
    const sims      = withMem.map(m => m.similarity).filter(v => v != null && parseFloat(v) > 0);
    const totalCases = withMem.reduce((s, m) => s + (m.cases || 0), 0);
    const stm        = withMem.reduce((s, m) => s + (m.recent || 0), 0);
    const ltm        = withMem.reduce((s, m) => s + (m.historical || 0), 0);
    const conflicts  = withMem.filter(m => m.conflict).length;
    const supports   = withMem.filter(m => m.supports === true).length;

    const avgHitRate = hitRates.length  ? hitRates.reduce((s, v) => s + v, 0) / hitRates.length : null;
    const avgSim     = sims.length      ? sims.reduce((s, v) => s + parseFloat(v), 0) / sims.length : null;

    return {
      mode: "session",
      targetHitRate: avgHitRate,
      similarity:    avgSim,
      cases:         totalCases,
      recent:        stm,
      historical:    ltm,
      evalPoints:    withMem.length,
      conflicts,
      supports,
      ev: null, // session aggregate EV not meaningful
    };

  }, [liveBars, mode]);

  // ── Empty state ─────────────────────────────────────────────────────────────
  if (!data) {
    return (
      <div style={{
        background: C.surface, border: `1px solid ${C.border}`,
        borderRadius: 10, padding: "12px 16px",
        display: "flex", alignItems: "center", gap: 10,
      }}>
        <div style={{ width: 6, height: 6, borderRadius: "50%", background: C.muted, opacity: 0.4 }} />
        <span style={{ fontFamily: MONO, fontSize: 10, color: C.muted }}>
          No memory data yet — waiting for first PROCEED evaluation
        </span>
      </div>
    );
  }

  const hitColor = hitRateColor(data.targetHitRate);
  const simCol   = simColor(data.similarity != null ? parseFloat(data.similarity) : null);

  // ── Compact layout (single row) ─────────────────────────────────────────────
  if (compact) {
    const simVal = data.similarity != null ? parseFloat(data.similarity) : null;
    return (
      <div style={{
        background: C.surface, border: `1px solid ${C.border}`,
        borderRadius: 8, padding: "8px 14px",
        display: "flex", alignItems: "center", gap: 16, flexWrap: "wrap",
      }}>
        {/* Eyebrow */}
        <span style={{ fontFamily: MONO, fontSize: 8, color: C.muted, letterSpacing: "1.2px" }}>
          MEMORY
        </span>

        {/* Hit rate */}
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <span style={{ fontFamily: MONO, fontSize: 9, color: C.muted }}>HIST WIN</span>
          <span style={{ fontFamily: MONO, fontSize: 13, color: hitColor, fontWeight: 500 }}>
            {data.targetHitRate != null ? pct(data.targetHitRate) : "—"}
          </span>
        </div>

        {/* Similarity */}
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <span style={{ fontFamily: MONO, fontSize: 9, color: C.muted }}>MATCH</span>
          <span style={{ fontFamily: MONO, fontSize: 13, color: simCol, fontWeight: 500 }}>
            {simVal != null ? pct(simVal) : "—"}
          </span>
        </div>

        {/* Cases */}
        {data.cases > 0 && (
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <span style={{ fontFamily: MONO, fontSize: 9, color: C.muted }}>CASES</span>
            <span style={{ fontFamily: MONO, fontSize: 13, color: C.body, fontWeight: 500 }}>
              {data.cases}
            </span>
          </div>
        )}

        {/* Supports badge */}
        {data.supports != null && (
          <span style={{
            fontFamily: MONO, fontSize: 8, fontWeight: 500,
            color: data.supports === true ? C.green : C.amber,
            background: data.supports === true ? `${C.green}15` : `${C.amber}15`,
            border: `1px solid ${data.supports === true ? C.green : C.amber}30`,
            borderRadius: 3, padding: "2px 7px", letterSpacing: "0.5px",
          }}>
            {data.supports === true ? "SUPPORTED" : "CONTRADICTED"}
          </span>
        )}

        {/* Conflict warning */}
        {data.conflict && (
          <span style={{
            fontFamily: MONO, fontSize: 8, color: C.amber,
            background: `${C.amber}15`, border: `1px solid ${C.amber}30`,
            borderRadius: 3, padding: "2px 7px",
          }}>
            ⚠ CONFLICT
          </span>
        )}

        {data.mode === "live" && data.time && (
          <span style={{ fontFamily: MONO, fontSize: 8, color: C.muted, marginLeft: "auto" }}>
            as of {data.time}
          </span>
        )}
      </div>
    );
  }

  // ── Full layout ──────────────────────────────────────────────────────────────
  const simVal = data.similarity != null ? parseFloat(data.similarity) : null;
  const stmGtLtm = (data.recent || 0) > (data.historical || 0);
  const memNote = data.mode === "session"
    ? (stmGtLtm
        ? "Primarily recent cases — adapting to current regime"
        : "Drawing on deep historical patterns — long-term moat active")
    : (data.note || (data.cases > 0
        ? `${data.cases} matching cases found in memory`
        : "Memory architecture active"));

  return (
    <div style={{
      background: C.surface, border: `1px solid ${C.border}`,
      borderRadius: 10, overflow: "hidden",
    }}>
      {/* Header */}
      <div style={{
        padding: "10px 16px",
        borderBottom: `1px solid ${C.border}`,
        display: "flex", alignItems: "center", justifyContent: "space-between",
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span style={{ fontSize: 10, color: C.purple }}>◑</span>
          <span style={{ fontFamily: MONO, fontSize: 9, color: C.muted, letterSpacing: "1.2px" }}>
            {data.mode === "live" ? "MEMORY · LAST PROCEED" : "MEMORY · SESSION AGGREGATE"}
          </span>
        </div>
        <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
          {data.conflict && (
            <span style={{
              fontFamily: MONO, fontSize: 8, color: C.amber,
              background: `${C.amber}15`, border: `1px solid ${C.amber}30`,
              borderRadius: 3, padding: "1px 6px",
            }}>
              ⚠ CONFLICT
            </span>
          )}
          {data.supports != null && (
            <span style={{
              fontFamily: MONO, fontSize: 8, fontWeight: 500,
              color: data.supports === true ? C.green : C.amber,
              background: data.supports === true ? `${C.green}15` : `${C.amber}15`,
              border: `1px solid ${data.supports === true ? C.green : C.amber}30`,
              borderRadius: 3, padding: "1px 6px",
            }}>
              {data.supports === true ? "SUPPORTS DECISION" : "CONTRADICTS DECISION"}
            </span>
          )}
          {data.mode === "live" && data.time && (
            <span style={{ fontFamily: MONO, fontSize: 8, color: C.muted }}>
              {data.time}
            </span>
          )}
        </div>
      </div>

      {/* Body */}
      <div style={{ padding: "14px 16px", display: "flex", flexDirection: "column", gap: 12 }}>

        {/* Bars */}
        <div style={{ display: "flex", gap: 20 }}>
          <MiniBar
            value={data.targetHitRate}
            color={hitColor}
            label="HIST WIN RATE"
          />
          <MiniBar
            value={simVal}
            color={simCol}
            label="PATTERN MATCH"
          />
          {data.ev != null && (
            <MiniBar
              value={Math.max(0, Math.min(1, (data.ev + 1) / 2))} // normalize EV to 0-1
              color={data.ev >= 0 ? C.green : C.red}
              label="EXPECTED VALUE"
            />
          )}
        </div>

        {/* Stats row */}
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          {data.cases > 0 && (
            <StatPill label="CASES" value={data.cases} color={C.body} />
          )}
          {data.recent > 0 && (
            <StatPill label="STM" value={data.recent} color={C.cyan} />
          )}
          {data.historical > 0 && (
            <StatPill label="LTM" value={data.historical} color={C.purple} />
          )}
          {data.mode === "session" && data.evalPoints != null && (
            <StatPill label="EVALS" value={data.evalPoints} color={C.body} />
          )}
          {data.mode === "session" && data.supports > 0 && (
            <StatPill
              label="SUPPORTED"
              value={`${data.supports}/${data.evalPoints}`}
              color={C.green}
            />
          )}
        </div>

        {/* Note */}
        <div style={{
          fontFamily: MONO, fontSize: 10, color: C.body, fontWeight: 300,
          lineHeight: 1.6, paddingTop: 2,
          borderTop: `1px solid ${C.border}`,
        }}>
          {memNote}
        </div>
      </div>
    </div>
  );
}